/**
 * The tour's one runtime. It follows the record's step, reads the world the
 * tour's hooks describe, feeds the machine, and carries out what each phase
 * means: make what the scene needs, lay the screen out, place the card once,
 * guard everything the scene does not allow, and stamp the record when the
 * step's fact holds. Steps are data; nothing else decides anything.
 */

import { orgAPI } from "@/api/api";
import { useCanvasFlowSessions } from "@/api/canvas";
import {
  useCreateFlowSession,
  useDeleteFlowSession,
} from "@/api/flow_sessions";
import { useAppNavigate, usePaneLocation } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { useSidebarContext } from "@/context/SidebarContext";
import Sentry from "@/integrations/Sentry";
import { useCanvasFloatingChat } from "@/pages/canvas/context/CanvasFloatingChatContext";
import { route } from "@/utils/route";
import type { FlowSession, UserOnboarding } from "@cohesive/api-client";
import { FLOW_SESSION_MODES } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useReactFlow } from "@xyflow/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { setPresentation } from "../presentation";
import { QUERY_KEY, useOnboardingStatus } from "../status";
import {
  dataPatch,
  exitPatch,
  finishPatch,
  readProgress,
  stepPatch,
  type TourProgress,
} from "../tour/progress";
import type {
  Action,
  CanvasGesture,
  CardStatus,
  Need,
  Scene,
  TourDefinition,
} from "../types";
import {
  HOLD_MS,
  QUIET_HOLD_MS,
  boxBetween,
  createMarquee,
  frames,
  measure,
  nodeAt,
  nodesIn,
  selectNodes,
  settled,
  type Marquee,
  type ScreenWants,
} from "./canvas";
import { COVERS_ATTR, HOT_ATTR, passStyles } from "./chrome";
import { Fallback, failureText } from "./Fallback";
import { installGuard } from "./guard";
import {
  SLIDE_MAX_PX,
  cardWidth,
  compose,
  travel,
  type CardPlace,
  type Dock,
  type Frame,
  type Geometry,
} from "./layout";
import { initialState, transition } from "./machine";
import {
  ATTEMPTS,
  BACKOFF_MS,
  Cancelled,
  NeedFailed,
  SESSION_NEED,
  ensureNeed,
  sleep,
  until,
  type Signal,
} from "./needs";
import { closeRail, releaseRail } from "./rail";
import {
  ENTER_MS,
  EXIT_MS,
  NUDGE_MS,
  SLIDE_MS,
  TourCard,
  type CardNote,
} from "./TourCard";
import { TourPill } from "./TourPill";
import { TourSpotlight } from "./TourSpotlight";

const SESSION_KEY = "onboardingSessionId";
const RECORD_NEED = "record";

/** How long the card shows a step done, counted from the person's action. */
export const BEAT_MS = 600;
/** The least a done card is on screen when the action was long ago. */
const MIN_CHECK_MS = 300;
/** The next card starts to enter no sooner than this after the action. */
const ENTER_FLOOR_MS = 980;
const SLOW_MS = 4_000;
const SET_TIMEOUT_MS = 25_000;
/** How long the product gets to land a commit before the tour makes it. */
const PATIENCE_MS = 6_000;
const PENDING_TIMEOUT_MS = 20_000;
const WORK_TIMEOUT_MS = 45_000;
/** How long a running turn gets to stop before the tour's chat is deleted. */
const RELEASE_TRIES = 15;
const RELEASE_EVERY_MS = 1_000;
const RESIZE_SETTLE_MS = 150;
const DRIFT_SETTLE_MS = 250;
const MAX_DRIFTS = 3;
const FRAME_MS = 300;
const DOUBLE_CLICK_MS = 500;
/** The deck pane opens on this much of the window, within its own limits. */
const PANE_FRACTION = 0.5;
const paneWidth = () => Math.round(window.innerWidth * PANE_FRACTION);
const DRAG_PX = 6;
/** Blocked actions before the card says how to leave. */
const HINT_AFTER = 3;
const SLOW_NOTE: CardNote = { kind: "slow", text: "Still working…" };
const EXIT_NOTE: CardNote = {
  kind: "nudge",
  text: "“Exit tour” at the top frees the app.",
};

// The tour's zoom: 0.75 at 1920×1080, proportionally less on smaller windows.
const REF_W = 1920;
const REF_H = 1080;
const BASE_ZOOM = 0.75;
const MIN_BASE_ZOOM = 0.45;

function baseZoom(): number {
  const scale = Math.min(
    1,
    window.innerWidth / REF_W,
    window.innerHeight / REF_H,
  );
  return Math.max(MIN_BASE_ZOOM, BASE_ZOOM * scale);
}

const NONE: readonly string[] = [];

const UNPRESSABLE = ':disabled, [aria-disabled="true"]';

/** Runs after the next paint; a hidden tab, which paints nothing, soon. */
function afterPaint(run: () => void) {
  let ran = false;
  const once = () => {
    if (ran) return;
    ran = true;
    run();
  };
  requestAnimationFrame(() => window.setTimeout(once, 0));
  window.setTimeout(once, 150);
}

/**
 * Deletes the tour's chat. A chat whose turn is still running cannot be
 * deleted, so the turn is stopped and the delete waits for it to let go.
 */
async function removeSession(
  id: string,
  remove: (id: string) => Promise<unknown>,
) {
  for (let i = 0; i < RELEASE_TRIES; i++) {
    const { data } = await orgAPI.flowSessions.getById(id);
    if (data.flow_session.status !== "processing") break;
    if (i === 0) await orgAPI.flowSessions.cancel(id);
    await sleep(RELEASE_EVERY_MS);
  }
  await remove(id);
}

const samePlace = (a: CardPlace, b: CardPlace) =>
  a.left === b.left && a.top === b.top && a.width === b.width;

const windowGeometry = (): Geometry => {
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  return {
    viewport,
    pane: { left: 0, top: 0, ...viewport },
    chat: null,
    rightPane: null,
    target: null,
    toolbar: null,
    controls: null,
  };
};

interface Placed {
  key: string;
  place: CardPlace;
}

interface Press {
  name: string;
  gesture: CanvasGesture<unknown>;
  commits: boolean;
  ids: readonly string[];
  x: number;
  y: number;
  marquee: Marquee | null;
  picked: readonly string[];
}

export function Director({
  tour,
  canvasId,
  progress,
}: {
  tour: TourDefinition;
  canvasId: string;
  progress: TourProgress;
}) {
  const status = useOnboardingStatus();
  const queryClient = useQueryClient();
  const reactFlow = useReactFlow();
  const chat = useCanvasFloatingChat();
  const createFlowSession = useCreateFlowSession();
  const deleteFlowSession = useDeleteFlowSession();
  const sessions = useCanvasFlowSessions(canvasId);
  const appNavigate = useAppNavigate();
  const rightPanePath = usePaneLocation("rightPane")?.pathname ?? null;
  const sidebar = useSidebarContext();

  const total = tour.scenes.length;
  const [state, dispatch] = useReducer(transition, undefined, () =>
    initialState(progress.step, total),
  );
  const { step, phase } = state;
  const scene: Scene<unknown> = tour.scenes[step - 1];
  const active = phase !== "exited" && phase !== "finished";
  const failed = phase === "failed";

  // ── The world ──────────────────────────────────────────────────────────
  const recordedSession = progress.data[SESSION_KEY] ?? null;
  const sessionId =
    recordedSession && sessions.data?.some((s) => s.id === recordedSession)
      ? recordedSession
      : null;

  const openFile = useCallback((fileRecordId: string) => {
    latest.current.appNavigate({
      pane: "rightPane",
      to: route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId }),
      options: {
        replace: true,
        analyticsSource: "onboarding",
        width: paneWidth(),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeFile = useCallback(() => {
    latest.current.appNavigate({
      pane: "rightPane",
      to: null,
      options: { replace: true, analyticsSource: "onboarding" },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openChat = useCallback(() => {
    const now = latest.current;
    const id = now.sessionId;
    if (id && !(now.chat.open && now.chat.currentSessionId === id))
      now.chat.openSession(id, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeChat = useCallback(() => {
    latest.current.chat.closeChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stage = tour.useStage({
    canvasId,
    data: progress.data,
    sessionId,
    openFile,
    closeFile,
    openChat,
    closeChat,
  });

  // Nothing about the card changes while a pointer is down, or while it is
  // leaving: the world it is shown, and everything decided from it, waits.
  const [pointerDown, setPointerDown] = useState(false);
  const shownWorld = useRef(stage.world);
  if (!pointerDown && phase !== "leaving") shownWorld.current = stage.world;
  const world = shownWorld.current;

  const allowed = phase === "live" ? scene.allowed(world) : NONE;
  const done = stage.ready && !!scene.done?.(world);

  const sessionNeed: Need<unknown> = useMemo(
    () => ({
      met: () => latest.current.sessionId !== null,
      ensure: async () => {
        const listed = await queryClient.fetchQuery({
          queryKey: ["canvas-flow-sessions", canvasId],
          queryFn: () => orgAPI.canvas.getFlowSessions(canvasId),
          staleTime: 0,
        });
        const recorded = latest.current.recordedSession;
        if (recorded && listed.some((s) => s.id === recorded)) return;
        // No harness: the tour is scripted and its composer is locked, so
        // there is no selector to read. It runs on cohesive, as written.
        const session = await latest.current.createSession({
          mode: FLOW_SESSION_MODES.CANVAS_ONBOARDING,
          class: "canvas",
          canvas_id: canvasId,
        });
        const kept = await latest.current.status.patch(
          dataPatch(tour.id, { [SESSION_KEY]: session.id }),
        );
        if (!kept) throw new Error("tour session not recorded");
      },
    }),
    [canvasId, queryClient, tour.id],
  );

  const pass = useMemo(
    () =>
      allowed.flatMap((name) => {
        const action = stage.actions[name];
        return action?.kind === "pass" ? action.selectors : [];
      }),
    [allowed, stage.actions],
  );

  const latest = useRef({
    state,
    scene,
    stage,
    world,
    allowed,
    pass,
    chat,
    sessionId,
    recordedSession,
    rightPanePath,
    appNavigate,
    progress,
    status,
    sidebar,
    createSession: createFlowSession.mutateAsync,
    deleteSession: deleteFlowSession.mutateAsync,
  });
  latest.current = {
    state,
    scene,
    stage,
    world,
    allowed,
    pass,
    chat,
    sessionId,
    recordedSession,
    rightPanePath,
    appNavigate,
    progress,
    status,
    sidebar,
    createSession: createFlowSession.mutateAsync,
    deleteSession: deleteFlowSession.mutateAsync,
  };

  // ── The card's place ───────────────────────────────────────────────────
  const cardRef = useRef<HTMLDivElement>(null);
  const contentKey = failed ? `${step}:failed` : `${step}`;
  const dock: Dock = failed ? "center" : scene.dock;
  const [width, setWidth] = useState(() => cardWidth(dock, windowGeometry()));
  const [placed, setPlaced] = useState<Placed | null>(null);
  const placedRef = useRef<Placed | null>(null);
  const [hidden, setHidden] = useState(false);
  const [sliding, setSliding] = useState(false);
  const up =
    placed?.key === contentKey &&
    !hidden &&
    (phase === "live" ||
      phase === "pending" ||
      phase === "done" ||
      phase === "failed");
  const upRef = useRef(up);
  upRef.current = up;

  const down = useRef(false);
  const onRelease = useRef<(() => void)[]>([]);
  const released = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (down.current) onRelease.current.push(resolve);
        else resolve();
      }),
    [],
  );

  const flow = useRef(reactFlow);
  flow.current = reactFlow;
  /** The canvas is still moving to its frame: nothing lands on it yet. */
  const framingUntil = useRef(0);
  /** Where that move is going, so the same frame is never asked for twice. */
  const framingTo = useRef<Frame | null>(null);
  /** One smooth move; a move already under way to this frame goes on. */
  const moveTo = useCallback((frame: Frame): boolean => {
    const seen = flow.current.getViewport();
    const same = (a: Frame, b: Frame) =>
      Math.abs(a.x - b.x) <= 1 &&
      Math.abs(a.y - b.y) <= 1 &&
      Math.abs(a.zoom - b.zoom) <= 0.002;
    if (same(seen, frame)) return false;
    const moving = performance.now() < framingUntil.current;
    if (moving && framingTo.current && same(framingTo.current, frame))
      return true;
    // An empty canvas has nothing to show moving.
    const duration = flow.current.getNodes().length ? FRAME_MS : 0;
    framingTo.current = frame;
    framingUntil.current = performance.now() + duration;
    void flow.current.setViewport(frame, { duration });
    return true;
  }, []);
  const place = useCallback(
    async (key: string, cardDock: Dock, wants: ScreenWants, signal: Signal) => {
      const geometry = measure(wants) ?? windowGeometry();
      const wide = cardWidth(cardDock, geometry);
      const before =
        placedRef.current?.key === key ? placedRef.current.place : null;
      const visible = !!before && upRef.current;
      // A new width is a new card: measured out of sight.
      const remeasure = !before || before.width !== wide;
      if (remeasure) {
        if (visible) {
          await released();
          setHidden(true);
          await sleep(EXIT_MS);
        }
        placedRef.current = null;
        // Committed here and now: the measure below must not queue behind
        // whatever the product is rendering.
        flushSync(() => {
          setPlaced(null);
          setWidth(wide);
        });
      }
      if (signal.cancelled) {
        setHidden(false);
        throw new Cancelled();
      }
      const el = cardRef.current;
      if (!el) return setHidden(false);
      const size = { width: el.offsetWidth, height: el.offsetHeight };
      const { scene: current, stage: now, state: at } = latest.current;
      const {
        place: next,
        frame,
        covers,
      } = compose({
        dock: cardDock,
        geometry,
        card: size,
        box:
          key === `${at.step}`
            ? (current.layout.frame?.(now.world) ?? null)
            : null,
        maxZoom: baseZoom(),
      });
      const area = { ...next, height: size.height };
      let crossing: string[] = [];
      // One smooth move, over before anything can land on the canvas.
      if (frame && moveTo(frame))
        crossing = nodesIn(
          area,
          flow.current.getNodes().map((node) => node.id),
        );

      const set = () => {
        placedRef.current = { key, place: next };
        setPlaced(placedRef.current);
        document.documentElement.setAttribute(COVERS_ATTR, covers.join(" "));
      };
      // A node on its way out from under the card: the card comes up once
      // it is clear, at the latest as the move ends.
      if (crossing.length && !visible) {
        const end = framingUntil.current - ENTER_MS / 2;
        while (performance.now() < end && nodesIn(area, crossing).length)
          await frames(1);
      }
      if (remeasure) {
        set();
        if (visible) await frames(2);
        return setHidden(false);
      }
      if (!visible) return set();
      if (samePlace(before, next)) return;
      await released();
      // One move: a short one slides, a long one goes out and comes back in.
      if (travel(before, next) <= SLIDE_MAX_PX) {
        setSliding(true);
        set();
        window.setTimeout(() => setSliding(false), SLIDE_MS + 60);
        return;
      }
      setHidden(true);
      await sleep(EXIT_MS);
      set();
      await frames(2);
      setHidden(false);
    },
    [moveTo, released],
  );

  // ── Setting the scene: enter, reload, return, resize and drift run this ─
  const needOf = useCallback(
    (name: string): Need<unknown> | undefined =>
      name === SESSION_NEED ? sessionNeed : latest.current.stage.needs[name],
    [sessionNeed],
  );
  const ensure = useCallback(
    (name: string, signal: Signal) =>
      ensureNeed({
        name,
        met: () => !!needOf(name)?.met(latest.current.stage.world),
        ensure: async () => {
          const need = needOf(name);
          if (!need) throw new Error(`unknown tour need: ${name}`);
          await need.ensure();
        },
        signal,
      }),
    [needOf],
  );

  const wantsOf = useCallback(
    (s: Scene<unknown>, w: unknown): ScreenWants => ({
      chat: s.layout.chat,
      rightPane: !!s.layout.rightPane?.(w),
      target: s.dock === "besideTarget" ? (s.spotlight?.(w) ?? null) : null,
    }),
    [],
  );

  /**
   * The canvas on its way to the next scene's frame while this card still
   * says done, once what the action opened has settled: the next card then
   * comes up on a canvas that is already still.
   */
  const preframing = useRef<Signal>(undefined);
  const preframe = useCallback(
    async (next: Scene<unknown>) => {
      if (preframing.current) preframing.current.cancelled = true;
      const signal: Signal = { cancelled: false };
      preframing.current = signal;
      const wants = wantsOf(next, latest.current.stage.world);
      await settled(wants, signal, HOLD_MS);
      if (signal.cancelled) return;
      const world = latest.current.stage.world;
      const box = next.layout.frame?.(world) ?? null;
      if (!box) return;
      const geometry = measure(wants) ?? windowGeometry();
      // The frame does not depend on the card's height while that is under
      // the room kept for a card under the canvas; the width is the dock's.
      const { frame } = compose({
        dock: next.dock,
        geometry,
        card: { width: cardWidth(next.dock, geometry), height: 0 },
        box,
        maxZoom: baseZoom(),
      });
      if (frame) moveTo(frame);
    },
    [moveTo, wantsOf],
  );

  /** Asks the product for the scene's chat and pane; true when it had to. */
  const applyLayout = useCallback((s: Scene<unknown>): boolean => {
    const now = latest.current;
    const w = now.stage.world;
    let moved = false;
    if (now.sidebar.railOpen) {
      closeRail(true, now.sidebar.setRailOpen);
      moved = true;
    }
    if (s.layout.chat === "tour") {
      const id = now.sessionId;
      if (id && !(now.chat.open && now.chat.currentSessionId === id)) {
        now.chat.openSession(id, "replace");
        moved = true;
      }
    } else if (now.chat.open || now.chat.currentSessionId) {
      now.chat.closeChat();
      moved = true;
    }

    const file = s.layout.rightPane?.(w) ?? null;
    const path = file
      ? route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId: file })
      : null;
    const open = now.rightPanePath;
    if (path ? !open?.startsWith(path) : !!open) {
      now.appNavigate({
        pane: "rightPane",
        to: path,
        options: {
          replace: true,
          analyticsSource: "onboarding",
          width: path ? paneWidth() : undefined,
        },
      });
      moved = true;
    }

    const selection = s.layout.select?.(w);
    if (selection && !s.done?.(w)) selectNodes(selection);
    return moved;
  }, []);

  const setting = useRef(false);
  /** The step last set on this page: the one before means a plain step on. */
  const lastSet = useRef(0);
  /** The action, or the fact, the last beat was counted from. */
  const beatFrom = useRef<{ step: number; at: number }>(undefined);
  const pending = useRef<{
    step: number;
    need: string;
    urgent: boolean;
    /** Times the fact was there and went away again. */
    fell: number;
  }>(undefined);
  const stampFailed = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    const signal: Signal = { cancelled: false };
    const s = tour.scenes[step - 1] as Scene<unknown>;
    setting.current = true;
    if (preframing.current) preframing.current.cancelled = true;
    void (async () => {
      try {
        while (
          !(await until(() => latest.current.stage.ready, signal, 60_000))
        ) {
          // The setting timeout speaks for a canvas that never loads.
        }
        for (const name of s.needs) await ensure(name, signal);
        const moved = applyLayout(s);
        await settled(
          wantsOf(s, latest.current.stage.world),
          signal,
          moved || lastSet.current !== step - 1 ? HOLD_MS : QUIET_HOLD_MS,
        );
        // A divider still being dragged: the scene is set where it is left.
        if (down.current) {
          await released();
          await settled(wantsOf(s, latest.current.stage.world), signal);
        }
        lastSet.current = step;
        const now = latest.current;
        const entering = now.state.phase === "setting";
        // Its fact already holds: no card to show, on to the stamp.
        if (!(entering && s.done?.(now.stage.world)))
          await place(`${step}`, s.dock, wantsOf(s, now.stage.world), signal);
        // A card that follows an action keeps its distance from it.
        const since = beatFrom.current;
        const early =
          entering && since?.step === step - 1
            ? since.at + ENTER_FLOOR_MS - performance.now()
            : 0;
        if (early > 0) await sleep(early);
        if (signal.cancelled) return;
        if (entering) dispatch({ type: "SET" });
        for (const name of s.prepare ?? [])
          void ensure(name, signal).catch(() => {});
      } catch (err) {
        if (err instanceof Cancelled) return;
        if (!(err instanceof NeedFailed)) Sentry.captureException(err);
        dispatch({
          type: "NEED_FAILED",
          need: err instanceof NeedFailed ? err.need : "scene",
        });
      } finally {
        if (!signal.cancelled) setting.current = false;
      }
    })();
    return () => {
      signal.cancelled = true;
    };
  }, [active, applyLayout, ensure, place, step, state.rev, tour, wantsOf]);

  // ── Following the record and the world ─────────────────────────────────
  const stamping = useRef<number | null>(null);
  useEffect(() => {
    if (stamping.current !== null && progress.step < stamping.current) return;
    dispatch({ type: "STEP", step: progress.step });
  }, [progress.step]);

  useEffect(() => {
    if (!stage.ready || !scene.done || pointerDown) return;
    dispatch({ type: "WORLD", done });
  }, [done, phase, pointerDown, scene, stage.ready, step]);

  // A commit whose fact fell away again (a send that was rolled back, a
  // retry after a failure): the tour makes it rather than asking twice.
  // An optimistic fact cancels the attempt that made it, so the tries are
  // counted here, after the product's own.
  useEffect(() => {
    const kept = pending.current;
    if (phase !== "live" || !kept || kept.step !== step || done) return;
    kept.fell += 1;
    if (kept.fell > ATTEMPTS)
      return dispatch({ type: "NEED_FAILED", need: kept.need });
    kept.urgent = true;
    dispatch({ type: "ACT", commits: true, completes: false });
  }, [done, phase, step]);

  useEffect(() => {
    if (phase !== "setting") return;
    const slow = window.setTimeout(() => dispatch({ type: "SLOW" }), SLOW_MS);
    const timeout = window.setTimeout(
      () => dispatch({ type: "TIMEOUT" }),
      SET_TIMEOUT_MS,
    );
    return () => {
      window.clearTimeout(slow);
      window.clearTimeout(timeout);
    };
  }, [phase, step, state.rev]);

  // The scene's own work: made while the card is up.
  const work = scene.work;
  const workMet =
    !work || !stage.ready || !!stage.needs[work.need]?.met(stage.world);
  useEffect(() => {
    if (phase !== "live" || !work || workMet) return;
    const entered = progress.enteredAt
      ? Date.parse(progress.enteredAt)
      : Number.NaN;
    const since = Number.isFinite(entered) ? Date.now() - entered : Infinity;
    // Cosmetic: the agent looks busy for a moment the first time through.
    // Never before the canvas has reached its frame.
    const delay = Math.max(
      Math.min(work.delayMs, Math.max(0, work.delayMs - since)),
      framingUntil.current - performance.now(),
    );
    const signal: Signal = { cancelled: false };
    const start = window.setTimeout(() => {
      ensure(work.need, signal).catch((err: unknown) => {
        if (err instanceof NeedFailed)
          dispatch({ type: "NEED_FAILED", need: err.need });
      });
    }, delay);
    const slow = window.setTimeout(
      () => dispatch({ type: "SLOW" }),
      delay + SLOW_MS,
    );
    const timeout = window.setTimeout(
      () => dispatch({ type: "TIMEOUT" }),
      delay + WORK_TIMEOUT_MS,
    );
    return () => {
      signal.cancelled = true;
      window.clearTimeout(start);
      window.clearTimeout(slow);
      window.clearTimeout(timeout);
    };
  }, [ensure, phase, progress.enteredAt, step, work, workMet]);

  // The person committed and the product is making it so. If the product
  // fails, or takes too long, the tour makes the same fact true itself.
  useEffect(() => {
    if (phase !== "pending") return;
    const kept = pending.current?.step === step ? pending.current : undefined;
    const signal: Signal = { cancelled: false };
    let following = false;
    const follow = () => {
      if (following || !kept) return;
      following = true;
      dispatch({ type: "SLOW" });
      ensure(kept.need, signal).catch((err: unknown) => {
        if (err instanceof NeedFailed)
          dispatch({ type: "NEED_FAILED", need: err.need });
      });
    };
    const patience = window.setTimeout(
      follow,
      kept?.urgent ? (BACKOFF_MS[kept.fell - 2] ?? 0) : PATIENCE_MS,
    );
    const slow = window.setTimeout(() => dispatch({ type: "SLOW" }), SLOW_MS);
    const timeout = window.setTimeout(
      () => dispatch({ type: "TIMEOUT" }),
      PENDING_TIMEOUT_MS,
    );
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.mutation.state.status === "error")
        follow();
    });
    return () => {
      signal.cancelled = true;
      window.clearTimeout(patience);
      window.clearTimeout(slow);
      window.clearTimeout(timeout);
      unsubscribe();
    };
  }, [ensure, phase, queryClient, step, state.rev]);

  // ── The beat between steps ─────────────────────────────────────────────
  const acted = useRef<{ step: number; at: number }>(undefined);
  const opening = useRef<{ step: number; run: () => void }>(undefined);
  useEffect(() => {
    if (phase !== "done") return;
    const now = performance.now();
    const at = acted.current?.step === step ? acted.current.at : now;
    const opens = opening.current;
    opening.current = undefined;
    if (opens?.step === step) {
      afterPaint(opens.run);
      const next = tour.scenes[step] as Scene<unknown> | undefined;
      if (next) void preframe(next).catch(() => {});
    }
    beatFrom.current = upRef.current ? { step, at } : undefined;
    const wait = upRef.current ? Math.max(at + BEAT_MS - now, MIN_CHECK_MS) : 0;
    let live = true;
    const t = window.setTimeout(() => {
      void released().then(() => {
        if (live) dispatch({ type: "BEAT" });
      });
    }, wait);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [phase, preframe, released, step, tour.scenes]);

  const persist = useCallback(async (patch: () => UserOnboarding) => {
    for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
      if (attempt > 0) await sleep(BACKOFF_MS[attempt - 1] ?? 0);
      if (await latest.current.status.patch(patch())) return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (phase !== "leaving") return;
    let live = true;
    let t: number | undefined;
    const leave = () => {
      if (step >= total) return dispatch({ type: "LEFT" });
      if (stampFailed.current === step)
        return dispatch({ type: "NEED_FAILED", need: RECORD_NEED });
      // Step N's stamp is written here, as step N is entered.
      const kept = latest.current.scene.record?.(latest.current.stage.world);
      stamping.current = step + 1;
      void persist(() => stepPatch(tour.id, step + 1, kept ?? {})).then(
        (ok) => {
          stamping.current = null;
          if (ok) return;
          stampFailed.current = step;
          dispatch({ type: "STEP", step: latest.current.progress.step });
        },
      );
      // The write shows in the cache at once; read it from there rather
      // than a render later.
      const written = readProgress(
        queryClient.getQueryData<UserOnboarding>(QUERY_KEY),
        tour.id,
      );
      if (written?.step === step + 1)
        dispatch({ type: "STEP", step: step + 1 });
    };
    if (placedRef.current?.key !== `${step}`) leave();
    // A little past the fade, counted from the paint that starts it, so the
    // next card's content is never seen here and nothing heavy runs under
    // the fade.
    else
      afterPaint(() => {
        if (live) t = window.setTimeout(leave, EXIT_MS + 20);
      });
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [persist, phase, queryClient, step, total, tour.id]);

  // ── The two ways the tour ends ─────────────────────────────────────────
  useEffect(() => {
    if (active) return;
    if (preframing.current) preframing.current.cancelled = true;
    const now = latest.current;
    const finished = now.state.phase === "finished";
    const id = now.sessionId;
    now.chat.closeChat();
    if (id) {
      // Out of the launcher's reach at once, gone when its turn lets it go.
      queryClient.setQueryData<FlowSession[]>(
        ["canvas-flow-sessions", canvasId],
        (list) => list?.filter((session) => session.id !== id),
      );
      void removeSession(id, now.deleteSession)
        .catch((err: unknown) => {
          // Best effort: an already-deleted session must not block the end.
          Sentry.captureException(
            err instanceof Error
              ? err
              : new Error("Failed to delete tour chat"),
          );
        })
        .finally(() => {
          void queryClient.invalidateQueries({
            queryKey: ["canvas-flow-sessions", canvasId],
          });
          queryClient.removeQueries({ queryKey: ["flowSession", id] });
        });
    }
    if (!finished) selectNodes([]);
    // Only a finished last step writes the done stamp; an exit never does.
    void persist(() => (finished ? finishPatch(tour.id) : exitPatch(tour.id)));
    if (finished) now.stage.after?.();
  }, [active, canvasId, persist, queryClient, tour.id]);

  // ── What the product is asked to hold while the tour runs ──────────────
  const spotlight =
    up && (phase === "live" || phase === "pending")
      ? (scene.spotlight?.(world) ?? null)
      : null;
  const prompt = scene.layout.prompt ?? null;
  useEffect(() => {
    if (!active) return;
    return setPresentation({
      viewportLocked: true,
      canvasChatHidden: true,
      suggestionsHidden: true,
    });
  }, [active]);
  useEffect(() => {
    if (!active) return;
    return setPresentation({
      composerPinnedPrompt: prompt,
      spotlightTarget: spotlight,
    });
  }, [active, prompt, spotlight]);

  useEffect(() => {
    if (!active) return;
    closeRail(
      latest.current.sidebar.railOpen,
      latest.current.sidebar.setRailOpen,
    );
    return () => releaseRail(latest.current.sidebar.setRailOpen);
  }, [active]);

  // ── Blocked actions, and the actions the scene allows ──────────────────
  const [nudgeTick, setNudgeTick] = useState(0);
  const [nudged, setNudged] = useState(false);
  const nudge = useCallback(() => {
    dispatch({ type: "BLOCKED" });
    setNudgeTick((tick) => tick + 1);
  }, []);
  useEffect(() => {
    if (nudgeTick === 0) return;
    setNudged(true);
    const t = window.setTimeout(() => setNudged(false), NUDGE_MS);
    return () => window.clearTimeout(t);
  }, [nudgeTick]);

  const act = useCallback((name: string, arg?: unknown) => {
    const now = latest.current;
    if (now.state.phase !== "live" || !now.allowed.includes(name)) return;
    if (performance.now() < framingUntil.current) return;
    const action: Action<unknown> | undefined = now.stage.actions[name];
    if (!action) return;
    if (action.kind === "pass") {
      // The product's own control does it; the guard reports the click.
      const control =
        document.querySelector<HTMLElement>(action.commit) ??
        document.querySelector<HTMLElement>(action.selectors[0] ?? "");
      if (control && !control.matches(UNPRESSABLE)) return control.click();
      // No control to press (Send gives way to Stop while the agent is
      // still on its last turn): the tour makes the fact itself.
      pending.current = {
        step: now.state.step,
        need: action.need,
        urgent: true,
        fell: 0,
      };
      acted.current = { step: now.state.step, at: performance.now() };
      dispatch({ type: "ACT", commits: true, completes: false });
      return;
    }
    if (action.commits || action.completes)
      acted.current = { step: now.state.step, at: performance.now() };
    // An action that ends the step shows its check first: what it sets off
    // in the product may be heavy, and runs once the check is painted.
    if (action.completes) afterPaint(() => action.run(now.stage.world, arg));
    else action.run(now.stage.world, arg);
    const { opens } = action;
    if (opens)
      opening.current = {
        step: now.state.step,
        run: () => opens(latest.current.stage.world),
      };
    dispatch({
      type: "ACT",
      commits: !!action.commits,
      completes: !!action.completes,
    });
  }, []);

  const press = useRef<Press | null>(null);
  const lastPick = useRef<{ id: string; at: number } | null>(null);
  const [viewportTick, setViewportTick] = useState(0);
  // The pointer cue on the canvas belongs to a live scene.
  useEffect(() => {
    if (phase !== "live") document.documentElement.removeAttribute(HOT_ATTR);
  }, [phase]);
  useEffect(() => {
    if (!active) return;
    return installGuard({
      config: () => ({
        pass: latest.current.pass,
        open: latest.current.state.phase === "live",
      }),
      onNudge: nudge,
      onEnter: () => {
        const [primary] = latest.current.allowed;
        if (primary) act(primary);
      },
      onPass: (target) => {
        const now = latest.current;
        for (const name of now.allowed) {
          const action = now.stage.actions[name];
          if (action?.kind !== "pass" || !target.closest(action.commit))
            continue;
          pending.current = {
            step: now.state.step,
            need: action.need,
            urgent: false,
            fell: 0,
          };
          acted.current = { step: now.state.step, at: performance.now() };
          dispatch({ type: "ACT", commits: true, completes: false });
          return;
        }
      },
      onPointer: (isDown) => {
        down.current = isDown;
        setPointerDown(isDown);
        if (isDown) return;
        const waiting = onRelease.current;
        onRelease.current = [];
        for (const resolve of waiting) resolve();
      },
      onCanvasDown: (e) => {
        const now = latest.current;
        for (const name of now.allowed) {
          const action = now.stage.actions[name];
          if (action?.kind !== "run" || !action.canvas) continue;
          press.current = {
            name,
            gesture: action.canvas,
            commits: !!action.commits,
            ids: action.canvas.nodes?.(now.stage.world) ?? NONE,
            x: e.clientX,
            y: e.clientY,
            marquee: null,
            picked: NONE,
          };
          return;
        }
        nudge();
      },
      canvasHot: (e) => {
        const now = latest.current;
        if (performance.now() < framingUntil.current) return false;
        for (const name of now.allowed) {
          const action = now.stage.actions[name];
          if (action?.kind !== "run" || !action.canvas) continue;
          if (action.canvas.on === "click") return true;
          const ids = action.canvas.nodes?.(now.stage.world) ?? NONE;
          return !!nodeAt(e.clientX, e.clientY, ids);
        }
        return false;
      },
      onResized: () => {
        window.setTimeout(() => {
          dispatch({ type: "RESIZE" });
          setViewportTick((tick) => tick + 1);
        }, RESIZE_SETTLE_MS);
      },
      onCanvasMove: (e) => {
        const p = press.current;
        if (!p?.gesture.marquee) return;
        const to = { x: e.clientX, y: e.clientY };
        if (!p.marquee) {
          if (Math.hypot(to.x - p.x, to.y - p.y) < DRAG_PX) return;
          p.marquee = createMarquee();
        }
        const area = boxBetween(p, to);
        p.marquee.draw(area);
        const ids = nodesIn(area, p.ids);
        if (ids.join() === p.picked.join()) return;
        p.picked = ids;
        selectNodes(ids);
      },
      onCanvasUp: (e) => {
        const p = press.current;
        press.current = null;
        if (!p) return;
        if (p.marquee) {
          p.marquee.remove();
          // A marquee that took nothing leaves a step it would commit as
          // it was.
          if (p.commits && !p.picked.length) return selectNodes([]);
          return act(p.name, p.picked);
        }
        if (p.gesture.on === "click") return act(p.name);
        const id = nodeAt(e.clientX, e.clientY, p.ids);
        if (!id) return nudge();
        if (p.gesture.on === "pick") return act(p.name, id);
        // A double-click is two clicks on the same node; the first selects
        // it, as the canvas would.
        const last = lastPick.current;
        const now = performance.now();
        if (last && last.id === id && now - last.at < DOUBLE_CLICK_MS) {
          lastPick.current = null;
          return act(p.name, id);
        }
        lastPick.current = { id, at: now };
        selectNodes([id]);
      },
    });
  }, [act, active, nudge]);

  // ── Resize and drift set the scene again, in place ─────────────────────
  useEffect(() => {
    let t: number | undefined;
    const onResize = () => {
      window.clearTimeout(t);
      t = window.setTimeout(() => {
        dispatch({ type: "RESIZE" });
        setViewportTick((tick) => tick + 1);
      }, RESIZE_SETTLE_MS);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const drifts = useRef({ step: 0, count: 0 });
  useEffect(() => {
    if ((phase !== "live" && phase !== "pending") || !stage.ready) return;
    if (setting.current) return;
    const chatFits =
      scene.layout.chat === "tour"
        ? !sessionId || (chat.open && chat.currentSessionId === sessionId)
        : !chat.open;
    const file = scene.layout.rightPane?.(stage.world) ?? null;
    const paneFits = file
      ? !!rightPanePath?.startsWith(
          route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId: file }),
        )
      : !rightPanePath;
    const needsMet = scene.needs.every(
      (name) => !!needOf(name)?.met(stage.world),
    );
    if (chatFits && paneFits && needsMet && !sidebar.railOpen) return;
    if (drifts.current.step !== step) drifts.current = { step, count: 0 };
    if (drifts.current.count >= MAX_DRIFTS) return;
    const t = window.setTimeout(() => {
      drifts.current.count += 1;
      dispatch({ type: "DRIFT" });
    }, DRIFT_SETTLE_MS);
    return () => window.clearTimeout(t);
  });

  // The fallback card stands in the middle of the window.
  useEffect(() => {
    if (!failed) return;
    const signal: Signal = { cancelled: false };
    place(
      `${step}:failed`,
      "center",
      { chat: "closed", rightPane: false, target: null },
      signal,
    ).catch(() => {});
    return () => {
      signal.cancelled = true;
    };
  }, [failed, place, step, viewportTick]);

  if (!active) return null;

  const exit = step < total ? () => dispatch({ type: "EXIT" }) : undefined;
  const cardStatus: CardStatus =
    phase === "setting"
      ? "setting"
      : phase === "live"
        ? "live"
        : phase === "pending"
          ? "pending"
          : "done";
  const note = failed
    ? null
    : state.slow && (phase !== "live" || !workMet)
      ? SLOW_NOTE
      : state.blocked >= HINT_AFTER && exit
        ? EXIT_NOTE
        : null;
  const pulsing = nudged
    ? allowed.flatMap((name) => {
        const action = stage.actions[name];
        return action?.kind === "run"
          ? (action.canvas?.nodes?.(world) ?? [])
          : [];
      })
    : NONE;
  const Card = scene.card;

  return (
    <>
      <TourPill
        step={step}
        total={total}
        working={phase === "setting" && state.slow}
        onExit={exit}
      />
      <TourSpotlight target={spotlight} emphasisTick={nudgeTick} />
      {pass.length > 0 && <style>{passStyles(pass)}</style>}
      {pulsing.length > 0 && (
        <style>{`
          @keyframes _co_tour_node_nudge {
            0%, 100% { outline-color: rgba(184, 110, 73, 0); }
            30% { outline-color: rgba(184, 110, 73, 0.9); }
          }
          ${pulsing
            .map((id) => `.react-flow__node[data-id="${CSS.escape(id)}"]`)
            .join(",")} {
            outline: 3px solid rgba(184, 110, 73, 0);
            outline-offset: 6px;
            animation: _co_tour_node_nudge ${NUDGE_MS}ms ease-out;
          }
        `}</style>
      )}
      <TourCard
        ref={cardRef}
        dock={dock}
        step={step}
        contentKey={contentKey}
        width={width}
        place={placed?.key === contentKey ? placed.place : null}
        shown={up}
        sliding={sliding}
        done={phase === "done" || phase === "leaving"}
        nudged={nudged}
        note={note}
      >
        {failed ? (
          <Fallback
            text={failureText(tour.failures, state.failure)}
            onRetry={() => {
              stampFailed.current = null;
              if (pending.current) pending.current.fell = 0;
              dispatch({ type: "RETRY" });
            }}
            onExit={exit}
          />
        ) : (
          <Card world={world} status={cardStatus} allowed={allowed} act={act} />
        )}
      </TourCard>
    </>
  );
}
